import type { Category, MenuResponse, Product, Subcategory } from '@cartazo/contracts'

/**
 * Aplana el árbol de la carta a índices de acceso directo.
 *
 * La navegación es un recorrido por coordenadas (categoría, subcategoría,
 * producto), y hacerlo sobre el árbol anidado obligaría a recorrerlo en cada
 * gesto. Aquí se paga una sola vez al cargar y después todo es O(1), que es lo
 * que permite que un swipe responda dentro del mismo frame.
 */

export type FlatProduct = {
  product: Product
  c: number
  s: number
  p: number
  categorySlug: string
  subcategorySlug: string
  /** Posición absoluta en la carta. Sirve para deducir la dirección de un salto. */
  ordinal: number
}

export type NormalizedMenu = {
  raw: MenuResponse
  categories: Category[]
  /** subcategorías[c] */
  subcategories: Subcategory[][]
  /** productos[c][s] */
  products: Product[][][]

  /** Todos los productos en orden de lectura, para búsqueda y saltos. */
  flat: FlatProduct[]
  byProductId: Map<string, FlatProduct>
  byPath: Map<string, FlatProduct>

  totalProducts: number
}

export function normalizeMenu(menu: MenuResponse): NormalizedMenu {
  const categories = menu.categories
  const subcategories: Subcategory[][] = []
  const products: Product[][][] = []
  const flat: FlatProduct[] = []
  const byProductId = new Map<string, FlatProduct>()
  const byPath = new Map<string, FlatProduct>()

  let ordinal = 0

  for (const [c, category] of categories.entries()) {
    subcategories[c] = category.subcategories

    // Se construye la fila entera y luego se asigna: evita indexar un array que
    // TypeScript, con `noUncheckedIndexedAccess`, considera posiblemente vacío.
    const rowOfProducts: Product[][] = []

    for (const [s, subcategory] of category.subcategories.entries()) {
      rowOfProducts[s] = subcategory.products

      for (const [p, product] of subcategory.products.entries()) {
        const entry: FlatProduct = {
          product,
          c,
          s,
          p,
          categorySlug: category.slug,
          subcategorySlug: subcategory.slug,
          ordinal: ordinal++,
        }
        flat.push(entry)
        byProductId.set(product.id, entry)
        byPath.set(pathKey(category.slug, subcategory.slug, product.slug), entry)
      }
    }

    products[c] = rowOfProducts
  }

  return {
    raw: menu,
    categories,
    subcategories,
    products,
    flat,
    byProductId,
    byPath,
    totalProducts: flat.length,
  }
}

export function pathKey(category: string, subcategory: string, product: string): string {
  return `${category}/${subcategory}/${product}`
}

/** Número de productos de una subcategoría. Acota el eje horizontal. */
export function productCount(menu: NormalizedMenu, c: number, s: number): number {
  return menu.products[c]?.[s]?.length ?? 0
}

/** Número de subcategorías de una categoría. Acota el eje vertical. */
export function subcategoryCount(menu: NormalizedMenu, c: number): number {
  return menu.subcategories[c]?.length ?? 0
}

export function productAt(menu: NormalizedMenu, c: number, s: number, p: number): Product | null {
  return menu.products[c]?.[s]?.[p] ?? null
}

export function subcategoryAt(menu: NormalizedMenu, c: number, s: number): Subcategory | null {
  return menu.subcategories[c]?.[s] ?? null
}

export function categoryAt(menu: NormalizedMenu, c: number): Category | null {
  return menu.categories[c] ?? null
}

/**
 * Texto indexable de un producto.
 *
 * Incluye `origin` porque ahí es donde el restaurante escribe los ingredientes
 * ("Pan brioche artesanal con carne Angus"), que es justo lo que el comensal
 * teclea cuando busca "brioche" o "angus".
 */
export function searchableText(product: Product): string {
  return (
    [product.name, product.origin, product.description]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase('es')
      .normalize('NFD')
      // Quita los diacríticos para que "jamon" encuentre "jamón". El comensal
      // teclea deprisa y con una mano.
      .replace(/[̀-ͯ]/g, '')
  )
}
